/**
 * Weekly Insights — the "Your week" review and "Where your weeks go"
 * trends (docs/WEEKLY_INSIGHTS_SPEC.md, approved 13 Oct 2026).
 *
 * Pure functions over the Notebook's frozen weeks (seam 2). Nothing here
 * stores data, touches the network or calls the AI: the review is computed
 * on the device from the user's own snapshot, so it can never invent a
 * fact, costs nothing, and needs no privacy change.
 *
 * Voice — the affirmation engine's rules (engine/affirmation.ts):
 * every line cites the user's own numbers; honest on rough weeks;
 * rollovers are information, not failure; never hollow praise.
 * Deterministic: the same week always reads the same.
 *
 * Classification rules:
 * - CORE = the task carries a tag the user has starred NOW (live flags by
 *   id — a judgement made today applies to history). Tag NAMES still
 *   resolve from the snapshot's frozen legend (seam 2 rule).
 * - NOT MINE = Zone 3 (urgent for others) or handed off.
 * - A core task is core even when it sits in Zone 3: it is still your job.
 * - Rollover counts in a snapshot are BEFORE the closing rollover (bind
 *   runs first — useStore.startWeekRollover). An open task at close is
 *   rolling once more, so its weeks-rolled = rolloverCount + 1, which is
 *   exactly the "Roll ×N" the board shows the next morning.
 */

import type { Task, WeekRecord, ZoneId } from '../types';
import { FATIGUE_THRESHOLD } from '../types';
import { carriedOf, completedOf, tagsOf, type WeekSnapshot } from './notebook';
import type { Tag } from './tags';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Fewer tasks than this on a week's board → one honest line, no statistics. */
export const THIN_WEEK_TASKS = 3;
/** Most facts a review shows. */
export const MAX_FACTS = 3;
/** Longest name the hand-off field keeps. */
export const HANDED_TO_MAX = 40;
/** Carried tasks at which a mostly-rolling board becomes the headline. */
export const LOAD_MIN_OPEN = 15;
/** Finished tasks needed in BOTH weeks before a Zone 2 % is compared. */
export const TREND_MIN_DONE = 3;
/** Weeks the trend chart shows. */
export const TREND_WEEKS = 8;

export type FactId =
  | 'thin'
  | 'core'
  | 'load'
  | 'stuck'
  | 'area'
  | 'notMine'
  | 'handoffs'
  | 'zone2'
  | 'finishCarry';

export interface Fact {
  id: FactId;
  text: string;
}

export interface WeekReview {
  weekId: string;
  facts: Fact[];
  question?: string;
  suggestion?: string;
  thin: boolean;
}

export function isCoreTask(task: Task, coreIds: ReadonlySet<string>): boolean {
  return (task.tags ?? []).some((id) => coreIds.has(id));
}

export function isNotMine(task: Task): boolean {
  return task.zone === 'zone3' || Boolean(task.handedTo?.trim());
}

/** Weeks this open-at-close task has now rolled (see header). */
export function weeksRolled(task: Task): number {
  return task.rolloverCount + 1;
}

/** Ids of the tags the user has starred, from the LIVE registry. */
export function coreTagIds(tags: Record<string, Tag>): Set<string> {
  return new Set(Object.values(tags).filter((t) => t.core).map((t) => t.id));
}

/** Trim and cap a hand-off name; empty → undefined (field removed). */
export function normaliseHandedTo(raw: string): string | undefined {
  const v = raw.replace(/\s+/g, ' ').trim().slice(0, HANDED_TO_MAX).trim();
  return v.length > 0 ? v : undefined;
}

function quote(text: string): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return `“${t.length > 60 ? `${t.slice(0, 57).trimEnd()}…` : t}”`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Tag names on these tasks, by how often they appear (ties: name order). */
function topTagNames(
  snap: WeekSnapshot,
  tasks: Task[],
  keep: (tag: Tag) => boolean,
  limit: number,
): string[] {
  const counts = new Map<string, number>();
  for (const t of tasks) {
    for (const tag of tagsOf(snap, t)) {
      if (keep(tag)) counts.set(tag.name, (counts.get(tag.name) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([name]) => name);
}

function zoneShare(tasks: Task[], zone: ZoneId): number {
  if (tasks.length === 0) return 0;
  return Math.round((tasks.filter((t) => t.zone === zone).length / tasks.length) * 100);
}

/** Finished tasks of a week — the Mastery record's count when it has one. */
export function finishedCount(snap: WeekSnapshot, records: Readonly<Record<string, WeekRecord>>): number {
  const rec = records[snap.id];
  return Math.max(completedOf(snap).length, rec?.completedCount ?? 0);
}

/** Zone 2 % of finished work — the Mastery record's figure when it has one. */
function zone2Share(snap: WeekSnapshot, records: Readonly<Record<string, WeekRecord>>): number {
  const rec = records[snap.id];
  return rec ? rec.distribution.zone2 : zoneShare(completedOf(snap), 'zone2');
}

/** weekHistory as a lookup for reviewWeek. */
export function recordsById(history: WeekRecord[]): Record<string, WeekRecord> {
  return Object.fromEntries(history.map((w) => [w.id, w]));
}

interface Candidate {
  fact: Fact;
  question: string;
  suggestion: string;
}

/**
 * The review of one closed week. `earlier` = older bound weeks, newest
 * first (only earlier[0] is read today — the Zone 2 comparison).
 *
 * `records` = the Mastery log (weekHistory) by week id. Totals and Zone 2
 * shares come from the RECORD when there is one, so the review never
 * contradicts the Mastery screen. Dry run 13 Oct: Week 40's snapshot held
 * 7 finished tasks while its record counted 8 — a task finished then
 * removed before the close is in the record, not on the board. Finished
 * work the snapshot cannot see counts as "not core" (no tags to read).
 */
export function reviewWeek(
  snap: WeekSnapshot,
  earlier: WeekSnapshot[],
  coreIds: ReadonlySet<string>,
  records: Readonly<Record<string, WeekRecord>> = {},
): WeekReview {
  const all = snap.tasks;
  if (all.length < THIN_WEEK_TASKS) {
    return {
      weekId: snap.id,
      thin: true,
      facts: [{ id: 'thin', text: 'A light week on the board — not enough to read into yet.' }],
    };
  }
  const done = completedOf(snap);
  const open = carriedOf(snap);
  const finished = finishedCount(snap, records);
  const out: Candidate[] = [];

  // 1. Core share — needs at least one ★ and a few finished tasks.
  if (coreIds.size > 0 && finished >= 3) {
    const core = done.filter((t) => isCoreTask(t, coreIds));
    const names = topTagNames(snap, core, (tag) => coreIds.has(tag.id), 2);
    const half = core.length * 2 >= finished;
    out.push({
      fact: {
        id: 'core',
        text:
          core.length === 0
            ? `None of the ${finished} things you finished touched your focus projects.`
            : `${core.length} of the ${finished} things you finished ${core.length === 1 ? 'was' : 'were'} focus work${
                names.length ? ` (${listNames(names)})` : ''
              }.`,
      },
      question: half
        ? 'What made room for your focus work this week?'
        : 'What pulled your week away from your focus projects?',
      suggestion: half
        ? 'Protect the same room next week — put one focus task in Monday’s Zone 2.'
        : 'Pick one focus task now and give it a 20-minute first step.',
    });
  }

  // 2. Load — a board so full that most of it keeps rolling. Dry run on
  //    real data (13 Oct): 50 carried, 40 rolled before; nothing else said it.
  const rolled = open.filter((t) => weeksRolled(t) >= 2);
  if (open.length >= LOAD_MIN_OPEN && rolled.length * 2 >= open.length) {
    out.push({
      fact: {
        id: 'load',
        text: `You carried ${open.length} tasks into the new week; ${rolled.length} of them had rolled before.`,
      },
      question: 'If you had to cut the board in half, what would go?',
      suggestion: 'Spend 10 minutes on your oldest rollers: finish, hand off or drop each one.',
    });
  }

  // 3. Longest-stuck task (fatigue threshold, counted as the board will show it).
  //    Repeat-aware: the same task topping last week's review too gets a
  //    sharper question instead of the same words again (dry run: one task
  //    led seven reviews in a row — a fact that never changes stops being read).
  const longest = (tasks: Task[]) =>
    tasks
      .filter((t) => !t.done && weeksRolled(t) >= FATIGUE_THRESHOLD)
      .sort((a, b) => weeksRolled(b) - weeksRolled(a) || a.createdAt - b.createdAt)[0];
  const stuck = longest(open);
  if (stuck) {
    const tag = tagsOf(snap, stuck)[0];
    const again = earlier[0] ? longest(earlier[0].tasks)?.id === stuck.id : false;
    out.push({
      fact: {
        id: 'stuck',
        text: again
          ? `${quote(stuck.text)} has rolled ${weeksRolled(stuck)} weeks — still your longest-stuck task.`
          : `${quote(stuck.text)} has rolled ${weeksRolled(stuck)} weeks — your longest-stuck task${
              tag ? `, tagged ${tag.name}` : ''
            }.`,
      },
      question: again
        ? `After ${weeksRolled(stuck)} weeks, is it still worth keeping as it is?`
        : 'Is it yours, or is it waiting on someone?',
      suggestion: again
        ? 'Decide it this week: split it into a first step, hand it off, or drop it.'
        : 'Give it a 20-minute first step in Monday’s Zone 2 — or hand it off.',
    });
  }

  // 4. Stuck area — the tag whose open tasks carry the most rollovers.
  if (rolled.length >= 2) {
    const byTag = new Map<string, { tasks: number; weeks: number }>();
    for (const t of rolled) {
      for (const g of tagsOf(snap, t)) {
        const row = byTag.get(g.name) ?? { tasks: 0, weeks: 0 };
        row.tasks += 1;
        row.weeks += weeksRolled(t);
        byTag.set(g.name, row);
      }
    }
    const [top] = [...byTag.entries()].sort((a, b) => b[1].weeks - a[1].weeks || a[0].localeCompare(b[0]));
    if (top && top[1].tasks >= 2) {
      const [area, { tasks, weeks }] = top;
      out.push({
        fact: {
          id: 'area',
          text: `${area} carries the most stuck work: ${tasks} tasks that have rolled ${weeks} weeks between them.`,
        },
        question: `What keeps ${area} work from finishing?`,
        suggestion: `Choose the one ${area} task that would unblock the rest and give it the first slot on Monday.`,
      });
    }
  }

  // 5. Not mine — finished work that came from other people's urgency.
  const notMineDone = done.filter((t) => isNotMine(t) && !isCoreTask(t, coreIds));
  if (notMineDone.length >= 2) {
    out.push({
      fact: {
        id: 'notMine',
        text: `${notMineDone.length} of the ${finished} things you finished were other people’s urgency (Zone 3 or handed off).`,
      },
      question: 'Which of these could someone else have owned from the start?',
      suggestion: 'Next time one lands, hand it off with a check-back date instead of doing it.',
    });
  }

  // 6. Hand-offs still out — and which come due in the week ahead.
  const handed = open.filter((t) => Boolean(t.handedTo?.trim()));
  if (handed.length > 0) {
    const horizon = snap.endedAt + 7 * DAY_MS;
    const due = handed.filter((t) => t.deferredUntil !== undefined && t.deferredUntil <= horizon).length;
    out.push({
      fact: {
        id: 'handoffs',
        text:
          due > 0
            ? `You have ${plural(handed.length, 'task')} with other people; ${due} ${
                due === 1 ? 'is' : 'are'
              } due a check-back this week.`
            : `You have ${plural(handed.length, 'task')} with other people.`,
      },
      question: 'Is anyone waiting on you to follow up?',
      suggestion: due > 0 ? 'Check back on the due ones first thing Monday.' : 'Give each a check-back date so none goes quiet.',
    });
  }

  // 7. Zone 2 trend against the previous closed week — only on enough
  //    finished work both weeks (dry run: "up from 0%" rested on 1 task).
  const prev = earlier[0];
  if (prev && finished >= TREND_MIN_DONE && finishedCount(prev, records) >= TREND_MIN_DONE) {
    const now = zone2Share(snap, records);
    const was = zone2Share(prev, records);
    const move = now === was ? `steady at ${now}%` : `${now}%, ${now > was ? 'up' : 'down'} from ${was}%`;
    out.push({
      fact: { id: 'zone2', text: `Strategic & Growth (Zone 2) was ${move} of your finished work.` },
      question: now >= was ? 'What helped you protect strategic time?' : 'What crowded out your strategic work?',
      suggestion: now >= was
        ? 'Keep the habit: name next week’s one Zone 2 priority today.'
        : 'Block one Zone 2 task before opening messages on Monday.',
    });
  }

  // 8. Fallback — always true, always available.
  out.push({
    fact: { id: 'finishCarry', text: `You finished ${finished} and carried ${open.length} into the new week.` },
    question: 'What is the one thing next week has to deliver?',
    suggestion: 'Put it in Zone 2 and give it a first step today.',
  });

  const chosen = out.slice(0, MAX_FACTS);
  return {
    weekId: snap.id,
    thin: false,
    facts: chosen.map((c) => c.fact),
    question: chosen[0].question,
    suggestion: chosen[0].suggestion,
  };
}

/** Newest bound week and the ones before it, newest first. */
export function weeksNewestFirst(bound: Record<string, WeekSnapshot>): WeekSnapshot[] {
  return Object.values(bound).sort((a, b) => b.endedAt - a.endedAt);
}

export interface WeekSplit {
  weekId: string;
  label: string;
  core: number;
  notMine: number;
  other: number;
}

/** "Where your weeks go": finished work per week, core > not mine > other. */
export function whereWeeksGo(
  newestFirst: WeekSnapshot[],
  coreIds: ReadonlySet<string>,
  records: Readonly<Record<string, WeekRecord>> = {},
  limit = TREND_WEEKS,
): WeekSplit[] {
  return newestFirst.slice(0, limit).map((snap) => {
    const split: WeekSplit = { weekId: snap.id, label: snap.label, core: 0, notMine: 0, other: 0 };
    const seen = completedOf(snap);
    for (const t of seen) {
      if (isCoreTask(t, coreIds)) split.core += 1;
      else if (isNotMine(t)) split.notMine += 1;
      else split.other += 1;
    }
    // Finished work the snapshot cannot see (see reviewWeek) — the bar's
    // total must equal the Mastery record.
    split.other += finishedCount(snap, records) - seen.length;
    return split;
  });
}

export interface StuckArea {
  tagId: string;
  name: string;
  /** Open tasks under this tag that have rolled at least once. */
  tasks: number;
  /** Sum of their rollover counts (live board numbers). */
  rollovers: number;
}

/** Live board: tags ranked by the rollovers their open tasks carry. */
export function stuckByArea(tasks: Task[], tags: Record<string, Tag>): StuckArea[] {
  const by = new Map<string, StuckArea>();
  for (const t of tasks) {
    if (t.done || t.rolloverCount < 1) continue;
    for (const id of t.tags ?? []) {
      const tag = tags[id];
      if (!tag) continue;
      const row = by.get(id) ?? { tagId: id, name: tag.name, tasks: 0, rollovers: 0 };
      row.tasks += 1;
      row.rollovers += t.rolloverCount;
      by.set(id, row);
    }
  }
  return [...by.values()].sort((a, b) => b.rollovers - a.rollovers || a.name.localeCompare(b.name));
}

export interface PlannedMinutes {
  tagId: string;
  name: string;
  minutes: number;
}

/** Live board: PLANNED minutes (open steps' estimates) per core tag — never "spent". */
export function plannedMinutesByCore(tasks: Task[], tags: Record<string, Tag>): PlannedMinutes[] {
  const rows: PlannedMinutes[] = [];
  for (const tag of Object.values(tags)) {
    if (!tag.core) continue;
    let minutes = 0;
    for (const t of tasks) {
      if (t.done || !(t.tags ?? []).includes(tag.id)) continue;
      for (const s of t.steps) if (!s.done) minutes += Math.max(0, s.minutes || 0);
    }
    rows.push({ tagId: tag.id, name: tag.name, minutes });
  }
  return rows.sort((a, b) => b.minutes - a.minutes || a.name.localeCompare(b.name));
}

/** Hand-offs on the live board whose check-back day has arrived. */
export function dueCheckBacks(tasks: Task[], now: number = Date.now()): Task[] {
  return tasks.filter(
    (t) => !t.done && Boolean(t.handedTo?.trim()) && t.deferredUntil !== undefined && t.deferredUntil <= now,
  );
}
